// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import {
  CheckRounded,
  ContentCopyRounded,
  DeleteRounded,
  DownloadRounded,
  EditRounded,
  InsertDriveFileOutlined,
  LaunchRounded,
  LinkRounded,
} from '@/constants/hardfin-standard-icons';
import { Attachment } from '@/file/api/types';
import { createViewableURLString } from '@/utilities/create-viewable-url-string';
import {
  IconButton,
  Link,
  Stack,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { PropsWithChildren, useState } from 'react';

interface Props {
  /**
   * An attachment (file or link) to display in the table.
   */
  attachment: Attachment;

  /**
   * If true, the row is disabled.
   */
  disabled?: boolean;

  /**
   * A callback fired when the delete button is pressed.
   */
  onDelete: () => void;

  /**
   * A callback fired when the edit button is pressed (links only).
   */
  onEdit?: () => void;
}

/**
 * A table row that displays an attachment's icon, name, date, and actions.
 * Handles both file uploads and URL links in a unified interface.
 */
export function AttachmentTableRow(props: PropsWithChildren<Props>) {
  const { attachment } = props;
  const [isCopied, setIsCopied] = useState(false);

  const isFile = attachment.type === 'file';
  const isLink = attachment.type === 'link';

  // For display name, use the name if available, otherwise fall back to URL for links
  const displayName = attachment.name || (isLink && attachment.url);

  // For files, we need to construct the download URL differently
  // For links, we use the direct URL
  const downloadURL = isFile
    ? (() => {
        try {
          const url = new URL(attachment.url);
          url.searchParams.set('attachment', 'true');
          return url;
        } catch {
          return null;
        }
      })()
    : null;

  const datedAtTooltipDateTimeString = attachment.datedAt.toLocaleString({
    dateStyle: 'long',
    timeStyle: 'long',
  });

  /**
   * Handles copying the link URL to clipboard with visual feedback
   */
  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(attachment.url);
      setIsCopied(true);
      // Reset the copied state after 1.5 seconds
      setTimeout(() => setIsCopied(false), 1500);
    } catch (error) {
      console.error('Failed to copy link:', error);
    }
  };

  return (
    <TableRow>
      {/* Icon Column (no header) */}
      <TableCell
        sx={{
          width: '48px',
          padding: '8px 16px',
        }}
      >
        <Tooltip title={isFile ? 'File' : 'Link'} arrow>
          {isFile ? (
            <InsertDriveFileOutlined
              fontSize="small"
              color="action"
              sx={{ display: 'flex', alignItems: 'center' }}
            />
          ) : (
            <LinkRounded
              fontSize="small"
              color="action"
              sx={{ display: 'flex', alignItems: 'center' }}
            />
          )}
        </Tooltip>
      </TableCell>

      {/* Name Column */}
      <TableCell>
        {(() => {
          // Get the safe URL for links, or use the file URL directly
          const safeURL = isLink
            ? createViewableURLString(attachment.url)
            : attachment.url;

          // If we have a safe URL, render as a link, otherwise render as plain text
          if (safeURL) {
            return (
              <Link href={safeURL} target="_blank" rel="noopener">
                {(isFile || attachment.name) && (
                  <Tooltip title={displayName || ''} arrow>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        maxWidth: '25rem',
                        color: 'text.secondary',
                      }}
                    >
                      {displayName}
                    </Typography>
                  </Tooltip>
                )}
                {isLink && (
                  <Tooltip title={attachment.url} arrow>
                    <Typography
                      variant={attachment.name ? 'caption' : 'body2'}
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        maxWidth: '25rem',
                        display: 'block',
                      }}
                    >
                      {attachment.url}
                    </Typography>
                  </Tooltip>
                )}
              </Link>
            );
          }

          // For unsafe URLs, render without link but show warning
          return (
            <div>
              {(isFile || attachment.name) && (
                <Tooltip title={displayName || ''} arrow>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      maxWidth: '25rem',
                      color: 'text.secondary',
                    }}
                  >
                    {displayName}
                  </Typography>
                </Tooltip>
              )}
              {isLink && (
                <Tooltip
                  title="URL is invalid or may be unsafe to link directly"
                  arrow
                >
                  <Typography
                    variant={attachment.name ? 'caption' : 'body2'}
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      maxWidth: '25rem',
                      display: 'block',
                      color: 'error.main',
                    }}
                  >
                    {attachment.url}
                  </Typography>
                </Tooltip>
              )}
            </div>
          );
        })()}
      </TableCell>

      {/* Update Date Column */}
      <TableCell>
        <Tooltip
          arrow
          title={`${isFile ? 'Uploaded' : 'Updated'} on ${datedAtTooltipDateTimeString}`}
        >
          <Typography variant="body2">
            {attachment.datedAt.toLocaleString({ dateStyle: 'medium' })}
          </Typography>
        </Tooltip>
      </TableCell>

      {/* Actions Column */}
      <TableCell>
        <Stack
          spacing={1}
          direction="row"
          alignItems="center"
          justifyContent="end"
        >
          {/* View/Download Action */}
          {isLink &&
            (() => {
              const safeURL = createViewableURLString(attachment.url);
              return safeURL ? (
                <Tooltip title="Open link" arrow>
                  <IconButton
                    href={safeURL}
                    size="small"
                    target="_blank"
                    rel="noopener"
                    disabled={props.disabled}
                    aria-label="Open link"
                  >
                    <LaunchRounded fontSize="small" />
                  </IconButton>
                </Tooltip>
              ) : null;
            })()}
          {isFile && attachment.url && (
            <Tooltip title="View file" arrow>
              <IconButton
                href={attachment.url}
                size="small"
                target="_blank"
                rel="noopener"
                disabled={props.disabled}
                aria-label="View file"
              >
                <LaunchRounded fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {isFile && downloadURL && (
            <Tooltip title="Download file" arrow>
              <IconButton
                href={downloadURL.toString()}
                size="small"
                disabled={props.disabled}
                aria-label="Download file"
              >
                <DownloadRounded fontSize="small" />
              </IconButton>
            </Tooltip>
          )}

          {/* Copy Action (links only) */}
          {isLink && (
            <Tooltip title={isCopied ? 'Copied!' : 'Copy link'} arrow>
              <IconButton
                size="small"
                onClick={handleCopyLink}
                aria-label={isCopied ? 'Copied' : 'Copy link'}
                sx={{
                  color: isCopied ? 'success.main' : 'action',
                  transition: 'color 0.2s ease-in-out',
                }}
              >
                {isCopied ? (
                  <CheckRounded fontSize="small" />
                ) : (
                  <ContentCopyRounded fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
          )}

          {/* Edit Action (links only) */}
          {isLink && props.onEdit && (
            <Tooltip title="Edit link" arrow>
              <IconButton
                size="small"
                onClick={props.onEdit}
                disabled={props.disabled}
                aria-label="Edit link"
              >
                <EditRounded fontSize="small" />
              </IconButton>
            </Tooltip>
          )}

          {/* Delete Action */}
          <Tooltip title={`Delete ${isFile ? 'file' : 'link'}`} arrow>
            <IconButton
              size="small"
              onClick={props.onDelete}
              disabled={props.disabled}
              aria-label={`Delete ${isFile ? 'file' : 'link'}`}
            >
              <DeleteRounded fontSize="small" />
            </IconButton>
          </Tooltip>
          {props.children}
        </Stack>
      </TableCell>
    </TableRow>
  );
}
